/**
 * ReservationsListMobile
 * Mobile hybrid list: compact row (guest, dates, channel) → tap to expand (nights, Owner Net, View details).
 * One row expanded at a time; "View details" opens BookingDetailModal.
 */

import { useState, useCallback } from 'react';
import { formatDateOnlyET, normalizeToYmd } from '../../utils/dateTimeET.js';
import { resolveMasterChannel } from '../../utils/masterChannelResolver.js';
import { getChannelColorVar } from '../../utils/channelColorUtils.js';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const ymd = normalizeToYmd(dateString);
  if (ymd) return formatDateOnlyET(ymd, { short: true });
  return 'N/A';
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

function getMasterChannelInfo(reservation) {
  return resolveMasterChannel(
    reservation.channel || '',
    reservation.canonical_financial_summary?.channel_subtype || null,
    reservation.status,
    reservation.clc_channels || null,
    reservation.canonical_financial_summary || null
  );
}

export default function ReservationsListMobile({
  reservations = [],
  onRowClick,
  summaryData = null,
}) {
  const [expandedId, setExpandedId] = useState(null);

  const handleRowClick = useCallback(
    (reservation, id) => {
      setExpandedId((prev) => (prev === id ? null : id));
    },
    []
  );

  const handleViewDetails = useCallback(
    (e, reservation) => {
      e.stopPropagation();
      onRowClick?.(reservation);
    },
    [onRowClick]
  );

  if (reservations.length === 0) {
    return (
      <div style={styles.emptyState}>
        <p style={styles.emptyText}>No reservations found for this reporting period.</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <ul style={styles.list} role="list">
        {reservations.map((reservation, idx) => {
          const id = reservation.reservation_id ?? reservation.id ?? idx;
          const isExpanded = expandedId === id;
          const masterChannel = getMasterChannelInfo(reservation);
          const checkIn = formatDate(reservation.check_in_date || reservation.checkin_date);
          const checkOut = formatDate(reservation.check_out_date || reservation.checkout_date);
          const nights = reservation.nights ?? 'N/A';
          const ownerNet =
            reservation.financials?.ownerNet != null
              ? reservation.financials.ownerNet
              : reservation.revenue ?? reservation.total_price ?? null;

          return (
            <li key={id} style={styles.listItem}>
              <button
                type="button"
                style={{
                  ...styles.rowButton,
                  ...(isExpanded ? styles.rowButtonExpanded : {}),
                }}
                onClick={() => handleRowClick(reservation, id)}
                aria-expanded={isExpanded}
              >
                <div style={styles.primaryLine}>
                  {reservation.guest_name || reservation.guestName || 'N/A'}
                </div>
                <div style={styles.secondaryLine}>
                  <span>{checkIn} → {checkOut}</span>
                  <span
                    style={{
                      ...styles.channelBadge,
                      backgroundColor: getChannelColorVar(masterChannel.master_channel_key),
                    }}
                  >
                    {masterChannel.master_channel_label}
                  </span>
                </div>
                {isExpanded && (
                  <div style={styles.expandedSection}>
                    <div style={styles.expandedRow}>
                      <span style={styles.expandedLabel}>Nights</span>
                      <span>{nights}</span>
                    </div>
                    <div style={styles.expandedRow}>
                      <span style={styles.expandedLabel}>Owner Net</span>
                      <span>
                        {ownerNet != null ? formatCurrency(ownerNet) : 'N/A'}
                      </span>
                    </div>
                    <button
                      type="button"
                      style={styles.viewDetailsButton}
                      onClick={(e) => handleViewDetails(e, reservation)}
                    >
                      View details
                    </button>
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {summaryData && (
        <div style={styles.footer}>
          {summaryData.rowCount} row{summaryData.rowCount !== 1 ? 's' : ''}
          {' · '}
          {summaryData.totalNights} nights
          {' · '}
          {summaryData.totalOwnerNet != null && summaryData.totalOwnerNet > 0
            ? formatCurrency(summaryData.totalOwnerNet)
            : '$0'}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    paddingBottom: 'var(--spacing-4)',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  listItem: {
    marginBottom: 'var(--spacing-2)',
  },
  rowButton: {
    width: '100%',
    padding: 'var(--spacing-3)',
    textAlign: 'left',
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '44px',
  },
  rowButtonExpanded: {
    borderColor: 'var(--color-border-medium)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  primaryLine: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-1)',
  },
  secondaryLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    flexWrap: 'wrap',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
  },
  channelBadge: {
    padding: '2px 6px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: '#fff',
  },
  expandedSection: {
    marginTop: 'var(--spacing-3)',
    paddingTop: 'var(--spacing-3)',
    borderTop: '1px solid var(--color-border-light)',
  },
  expandedRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 'var(--font-size-sm)',
    marginBottom: 'var(--spacing-2)',
  },
  expandedLabel: {
    color: 'var(--color-text-secondary)',
  },
  viewDetailsButton: {
    marginTop: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-bg-primary)',
    backgroundColor: 'var(--color-brand-primary, #0f4c75)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    width: '100%',
    minHeight: '44px',
  },
  footer: {
    padding: 'var(--spacing-3) var(--spacing-4)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    borderTop: '1px solid var(--color-border-light)',
  },
  emptyState: {
    padding: 'var(--spacing-8)',
    textAlign: 'center',
    color: 'var(--color-text-tertiary)',
    fontSize: 'var(--font-size-sm)',
  },
  emptyText: {
    margin: 0,
  },
};
